// TODO: Move this component to the shared UI library
import React, { ReactNode, useCallback, useState } from "react";
import { useDropzone, DropzoneOptions } from "react-dropzone";
import styled from "@emotion/styled";
import { CircularProgress, Box } from "@avenue-8/sidekick-ui";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { InlineError } from "./InlineError";
import { IconButton } from "@mui/material";

const allowedFileTypes = {
  jpeg: {
    name: "JPEG",
    mimeType: "image/jpeg",
    extensions: [".jpeg", ".jpg"],
  },
  png: {
    name: "PNG",
    mimeType: "image/png",
    extensions: [".png"],
  },
} as const;
interface SinglePhotoUploaderProps {
  children: ReactNode;
  onFileAccepted?: (file: File) => void;
  onFileRejected?: (rejectedFiles: any[]) => void;
  onFileRemoved?: () => void;
  maxSize?: number;
  accept?: (keyof typeof allowedFileTypes)[];
  disabled?: boolean;
  dropzoneOptions?: Partial<DropzoneOptions>;
  isLoading?: boolean;
  serverError?: Error | null;
  resetServerError?: () => void;
}

interface DropzoneContainerProps {
  $disabled: boolean;
  $isDragActive: boolean;
  $isDragAccept: boolean;
  $isError: boolean;
  $isLoading?: boolean;
}

const DropzoneContainer = styled.div<DropzoneContainerProps>`
  position: relative;
  isolation: isolate;
  border-radius: 0.5rem;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease-in-out;
  outline: none;
  img {
    display: block;
  }
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  pointer-events: ${(props) =>
    props.$disabled || props.$isLoading ? "none" : "auto"};
  opacity: ${(props) => (props.$isLoading ? 0.5 : 1)};

  &::before {
    content: "";
    position: absolute;
    top: -0.5rem;
    left: -0.5rem;
    right: -0.5rem;
    bottom: -0.5rem;
    border: 1px dashed
      ${(p) => {
        if (p.$disabled || p.$isLoading) return p.theme.colors.neutral;
        if (p.$isError) return p.theme.colors.error;
        if (p.$isDragAccept || p.$isDragActive) return p.theme.colors.success;
        return "transparent";
      }};
    border-radius: inherit;
    background-color: ${(p) => {
      if (p.$disabled || p.$isLoading) return `${p.theme.colors.neutral}22`;
      if (p.$isError) return `${p.theme.colors.error}22`;
      if (p.$isDragAccept || p.$isDragActive)
        return `${p.theme.colors.success}22`;
      return "transparent";
    }};
    pointer-events: none;
    transition: all 0.2s ease-in-out;
    z-index: -1;
  }

  .photo-actions {
    position: absolute;
    border-radius: 0.5rem;
    opacity: 0;
    transition: 200ms;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    gap: 0.25rem;
    background-color: transparent;

    button {
      border-radius: 0.5rem;
    }
  }

  &:hover {
    .photo-actions {
      opacity: 1;
      background-color: ${(p) => p.theme.button.outlined.colors.background};
    }
  }

  &:hover::before {
    ${(p) =>
      !p.$disabled && !p.$isDragActive && !p.$isDragAccept && !p.$isError
        ? `
          border-color: ${p.theme.colors.border};
          background-color: ${p.theme.colors.border}11;
        `
        : ""}
  }
`;

const AbsoluteCircularProgress = styled(CircularProgress)`
  position: absolute;
  top: calc(50% - 0.75rem);
  left: calc(50% - 0.75rem);
`;

const ErrorContainer = styled(Box)`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
`;

export const SinglePhotoUploader: React.FC<SinglePhotoUploaderProps> = (
  props,
) => {
  const {
    children,
    onFileAccepted,
    onFileRejected,
    onFileRemoved,
    maxSize = 5 * 1024 * 1024, // 5MB default
    accept = ["jpeg", "png"],
    disabled = false,
    dropzoneOptions = {},
    isLoading,
    serverError,
    resetServerError,
  } = props;
  const [errors, setErrors] = useState<string[] | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      if (disabled) return;

      if (rejectedFiles.length > 0) {
        const allErrors: string[] = [];

        rejectedFiles.forEach((rejection) => {
          rejection.errors.forEach((error: any) => {
            switch (error.code) {
              case "file-too-large":
                allErrors.push(
                  `File "${rejection.file.name}" is too large (max ${Math.round(maxSize / (1024 * 1024))}MB)`,
                );
                break;
              case "file-invalid-type": {
                const acceptedTypeNames = accept
                  .map((fileType) => {
                    return allowedFileTypes[fileType].name;
                  })
                  .join(", ");
                allErrors.push(
                  `File "${rejection.file.name}" is not supported. Only ${acceptedTypeNames} images are allowed`,
                );
                break;
              }
              case "too-many-files":
                allErrors.push("Only one file allowed");
                break;
              default:
                allErrors.push(
                  `File "${rejection.file.name}": ${error.message}`,
                );
                break;
            }
          });
        });

        const uniqueErrors = [...new Set(allErrors)];
        setErrors(uniqueErrors);
        onFileRejected?.(rejectedFiles);
        return;
      }

      if (errors) setErrors(null);

      if (acceptedFiles.length > 0) {
        onFileAccepted?.(acceptedFiles[0]);
      }
    },
    [disabled, errors, onFileRejected, maxSize, onFileAccepted, accept],
  );

  const clearErrors = useCallback(() => {
    if (errors) {
      setErrors(null);
    }
  }, [errors]);

  const getAcceptFiles = useCallback(() => {
    return accept.reduce(
      (acc, fileType) => {
        const fileConfig = allowedFileTypes[fileType];
        if (fileConfig) {
          acc[fileConfig.mimeType] = [...fileConfig.extensions];
        }
        return acc;
      },
      {} as Record<string, string[]>,
    );
  }, [accept]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    onDragEnter: () => {
      clearErrors();
    },
    accept: getAcceptFiles(),
    maxSize,
    disabled,
    maxFiles: 1,
    ...dropzoneOptions,
  });

  const handleContainerClick = useCallback(() => {
    clearErrors();
  }, [clearErrors]);

  return (
    <DropzoneContainer
      {...getRootProps({
        "aria-describedby": "dropzone-description-single",
        role: "button",
        tabIndex: disabled ? -1 : 0,
        onClick: handleContainerClick,
      })}
      $disabled={disabled}
      $isDragActive={isDragActive}
      $isDragAccept={isDragAccept}
      $isError={
        (isDragReject && isDragActive) || !!errors?.length || !!serverError
      }
      $isLoading={isLoading}
    >
      <input {...getInputProps()} />
      {children}
      {errors && errors.length > 0 && (
        <ErrorContainer>
          {errors.map((error, index) => (
            <InlineError
              key={`error-${index}`}
              message={error}
              onClose={clearErrors}
            />
          ))}
        </ErrorContainer>
      )}
      {!!serverError && (
        <ErrorContainer>
          <InlineError
            message={
              "Unexpected error. Please try again, and contact support if the error persists."
            }
            onClose={resetServerError}
          />
        </ErrorContainer>
      )}
      <div className="photo-actions">
        <IconButton>
          <AddAPhotoIcon fontSize="small" />
        </IconButton>
        {!!onFileRemoved && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onFileRemoved();
            }}
          >
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      {isLoading && <AbsoluteCircularProgress size={24} />}
    </DropzoneContainer>
  );
};
